/**
 * Google Analytics 4 integration via Google Tag Manager.
 *
 * Loads the GTM container script when VITE_GTM_CONTAINER_ID is present
 * at build time.  The GA4 tag is configured inside the GTM container
 * (not in code), so only the container ID is needed here.
 *
 * Events are pushed to the dataLayer as plain objects.  The GTM container
 * picks them up and forwards them to GA4 (or any other tag configured
 * in the container).
 *
 * GA4/GTM is completely optional.  If the env var is missing the module
 * is a no-op.
 */

const GTM_CONTAINER_ID = (() => {
  try {
    return import.meta.env.VITE_GTM_CONTAINER_ID || "";
  } catch {
    return "";
  }
})();

let scriptLoaded = false;

function _loadGtmContainer(containerId: string): void {
  if (scriptLoaded || typeof window === "undefined") return;
  scriptLoaded = true;

  // Standard GTM dataLayer setup — must happen *before* the container
  // script loads so that queued events are processed on arrival.
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

/**
 * Initialise GTM if a container ID is configured.
 * Call once at app startup (e.g. in App.tsx).
 */
export function initGoogleAnalytics(): void {
  if (!GTM_CONTAINER_ID) return;
  _loadGtmContainer(GTM_CONTAINER_ID);
}

/**
 * Track a page view in GA4.
 * Call this on every client-side route change.
 */
export function trackPageView(path: string): void {
  if (!GTM_CONTAINER_ID || typeof window === "undefined") return;

  const dl = (window as any).dataLayer;
  if (!dl) return;

  dl.push({
    event: "page_view",
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
  });
}

/**
 * Track a custom event in GA4.
 * Re-uses the same event names as the backend analytics funnel.
 */
export function trackGAEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!GTM_CONTAINER_ID || typeof window === "undefined") return;

  const dl = (window as any).dataLayer;
  if (!dl) return;

  dl.push({
    event: eventName,
    ...params,
  });
}
